import React, { useState, useMemo, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { format, startOfMonth, endOfMonth, eachDayOfInterval, getDay, addMonths, subMonths } from "date-fns";
import { motion } from "framer-motion";
import {
  Calendar,
  ChevronLeft,
  ChevronRight,
  Download,
  FileSpreadsheet,
  Printer,
  Search,
  Users,
  CheckCircle2,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { getCooperativeLogoSrc } from "@/components/ui/cooperative-logo";

interface Teacher {
  id: number;
  fullName: string;
  teacherId: string;
  teacherGroup?: {
    id: number;
    name: string;
  };
}

interface Meeting {
  id: number;
  meetingDate: string;
  startTime: string;
  endTime: string;
  location: string;
  attendances: Attendance[];
}

interface Attendance {
  id: number;
  isPresent: boolean;
  excuse: string;
  teacher: {
    id: number;
    fullName: string;
    teacherId: string;
  };
}

interface TeacherGroup {
  id: number;
  name: string;
  meetingDay?: string;
  meetingFrequency?: string;
}

export default function Attendance() {
  const [selectedGroupId, setSelectedGroupId] = useState<number | null>(null);
  const [selectedMonth, setSelectedMonth] = useState<Date>(new Date());
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedMemberFilter, setSelectedMemberFilter] = useState<string>("all");
  const [attendanceData, setAttendanceData] = useState<Record<string, boolean>>({});

  const queryClient = useQueryClient();

  // Fetch saving groups and auto-select the first (only) group
  const { data: groups = [] } = useQuery(
    ["teacher-groups"],
    async () => {
      const { data } = await api.get("/teacher-groups");
      return data?.data || [];
    },
    {
      onSuccess: (groups) => {
        // Auto-select first group (single group project)
        if (groups.length > 0) {
          setSelectedGroupId(groups[0].id);
        }
      },
    }
  );

  // Fetch selected group details
  const { data: selectedGroup } = useQuery(
    ["teacher-group", selectedGroupId],
    async () => {
      if (!selectedGroupId || selectedGroupId === null || selectedGroupId === undefined) {
        return null;
      }
      const { data } = await api.get(`/teacher-groups/${selectedGroupId}`);
      return data?.data;
    },
    {
      enabled: !!selectedGroupId && selectedGroupId !== null && selectedGroupId !== undefined,
    }
  );

  // Fetch teachers for the selected group
  const { data: allTeachers = [] } = useQuery(
    ["teachers"],
    async () => {
      const { data } = await api.get("/teachers");
      // Handle different response structures
      const teachers = data?.data || data?.results || data || [];
      return Array.isArray(teachers) ? teachers : [];
    }
  );

  // Filter teachers by selected group
  const teachers = useMemo(() => {
    if (!selectedGroupId || !allTeachers.length) return [];
    
    const filtered = allTeachers.filter((teacher: any) => {
      // Check if teacher has teacherGroup and it matches selectedGroupId
      const teacherGroupId = teacher.teacherGroup?.id || teacher.teacherGroupId;
      return teacherGroupId === selectedGroupId;
    });
    
    return filtered;
  }, [allTeachers, selectedGroupId]);

  // Fetch all meetings
  const { data: allMeetings = [] } = useQuery(
    ["meetings"],
    async () => {
      const { data } = await api.get("/meetings");
      return data?.data || [];
    },
    {
      enabled: !!selectedGroupId,
    }
  );

  // Filter meetings for the selected month
  const monthStart = startOfMonth(selectedMonth);
  const monthEnd = endOfMonth(selectedMonth);
  
  const meetings = useMemo(() => {
    return allMeetings.filter((meeting: Meeting) => {
      const meetingDate = new Date(meeting.meetingDate);
      return meetingDate >= monthStart && meetingDate <= monthEnd;
    });
  }, [allMeetings, monthStart, monthEnd]);

  // Initialize attendance data when meetings change
  useEffect(() => {
    const initialData: Record<string, boolean> = {};
    meetings.forEach((meeting: Meeting) => {
      const dateKey = format(new Date(meeting.meetingDate), "yyyy-MM-dd");
      meeting.attendances?.forEach((attendance: Attendance) => {
        const key = `${attendance.teacher.id}-${dateKey}`;
        initialData[key] = attendance.isPresent;
      });
    });
    setAttendanceData(initialData);
  }, [meetings]);

  // Get all dates in the selected month
  const monthDates = useMemo(() => {
    return eachDayOfInterval({ start: monthStart, end: monthEnd });
  }, [monthStart, monthEnd]);

  // Filter teachers based on search query
  const filteredTeachers = useMemo(() => {
    let filtered = teachers;
    
    if (searchQuery) {
      filtered = filtered.filter((teacher: Teacher) =>
        teacher.fullName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        teacher.teacherId.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    if (selectedMemberFilter === "present") {
      // Show only members who have at least one present attendance
      filtered = filtered.filter((teacher: Teacher) => {
        return monthDates.some((date) => {
          const dateKey = format(date, "yyyy-MM-dd");
          const key = `${teacher.id}-${dateKey}`;
          return attendanceData[key] === true;
        });
      });
    } else if (selectedMemberFilter === "absent") {
      // Show only members who have at least one absent attendance
      filtered = filtered.filter((teacher: Teacher) => {
        return monthDates.some((date) => {
          const dateKey = format(date, "yyyy-MM-dd");
          const key = `${teacher.id}-${dateKey}`;
          return attendanceData[key] === false;
        });
      });
    }

    return filtered;
  }, [teachers, searchQuery, selectedMemberFilter, monthDates, attendanceData]);

  // Count scheduled meetings in the month
  const scheduledMeetingsCount = meetings.length;

  // Update attendance mutation
  const updateAttendanceMutation = useMutation(
    async ({ meetingId, teacherId, isPresent }: { meetingId: number; teacherId: number; isPresent: boolean }) => {
      const { data } = await api.patch(`/meetings/${meetingId}/attendance/${teacherId}`, {
        isPresent,
        excuse: "",
      });
      return data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries("meetings");
        toast.success("Attendance updated successfully");
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.message || "Failed to update attendance");
      },
    }
  );

  const handleAttendanceChange = async (teacherId: number, date: Date, checked: boolean) => {
    const dateKey = format(date, "yyyy-MM-dd");
    const key = `${teacherId}-${dateKey}`;
    
    // Update local state immediately
    setAttendanceData((prev) => ({
      ...prev,
      [key]: checked,
    }));

    // Find or create meeting for this date
    let meeting = meetings.find((m: Meeting) => 
      format(new Date(m.meetingDate), "yyyy-MM-dd") === dateKey
    );

    if (!meeting) {
      // Create a new meeting if it doesn't exist
      try {
        const { data } = await api.post("/meetings", {
          meetingDate: dateKey,
          startTime: "10:00",
          endTime: "12:00",
          location: selectedGroup?.name || "TBA",
          agenda: "",
        });
        meeting = data.data;
        queryClient.invalidateQueries("meetings");
      } catch (error: any) {
        toast.error(error.response?.data?.message || "Failed to create meeting");
        // Revert local state
        setAttendanceData((prev) => ({
          ...prev,
          [key]: !checked,
        }));
        return;
      }
    }

    // Update attendance
    updateAttendanceMutation.mutate({
      meetingId: meeting.id,
      teacherId,
      isPresent: checked,
    });
  };

  const handlePreviousMonth = () => {
    setSelectedMonth(subMonths(selectedMonth, 1));
  };

  const handleNextMonth = () => {
    setSelectedMonth(addMonths(selectedMonth, 1));
  };

  const handleToday = () => {
    setSelectedMonth(new Date());
  };

  const handleExportPDF = async () => {
    try {
      // Dynamically import jsPDF and html2canvas
      const jsPDF = (await import('jspdf')).jsPDF;
      const html2canvas = (await import('html2canvas')).default;
      
      const startDate = format(monthStart, 'MMMM d, yyyy');
      const endDate = format(monthEnd, 'MMMM d, yyyy');
      
      // Create a hidden div for PDF content
      const pdfDiv = document.createElement('div');
      pdfDiv.style.position = 'fixed';
      pdfDiv.style.left = '-9999px';
      pdfDiv.style.top = '0';
      pdfDiv.style.width = '1400px';
      pdfDiv.style.background = '#fff';
      pdfDiv.style.fontFamily = 'Arial, sans-serif';
      pdfDiv.style.padding = '40px';
      
      // Build table rows
      const tableRows = filteredTeachers.map((teacher: Teacher, index: number) => {
        const dateCells = monthDates.map((date) => {
          const dateKey = format(date, "yyyy-MM-dd");
          const key = `${teacher.id}-${dateKey}`;
          const isChecked = attendanceData[key] || false;
          return `<td style="border: 1px solid #ddd; padding: 6px; text-align: center;">${isChecked ? '✓' : ''}</td>`;
        }).join('');
        
        return `<tr>
          <td style="border: 1px solid #ddd; padding: 6px; text-align: center;">${index + 1}</td>
          <td style="border: 1px solid #ddd; padding: 6px; font-weight: 500;">${teacher.fullName}</td>
          ${dateCells}
        </tr>`;
      }).join('');
      
      // Build date headers
      const dateHeaders = monthDates.map((date) => {
        const dayName = format(date, "EEE");
        const dayNumber = format(date, "d");
        return `<th style="background-color: #4dbd07; color: white; padding: 8px 4px; text-align: center; font-weight: 600; border: 1px solid #3d9a06; font-size: 10px;">
          <div>${dayName.toUpperCase()}</div>
          <div style="font-weight: bold;">${dayNumber}</div>
        </th>`;
      }).join('');
      
      pdfDiv.innerHTML = `
        <div style="margin-bottom: 30px;">
          <div style="display: flex; align-items: center; margin-bottom: 15px;">
            <div style="width: 120px; margin-right: 20px; display: flex; flex-direction: column; align-items: center;">
              <img src={getCooperativeLogoSrc()} alt="IKIMINA Logo" style="max-width: 120px; height: auto; object-fit: contain;" />
            </div>
            <div style="flex: 1;">
              <div style="font-size: 24px; font-weight: bold; color: #000; margin-bottom: 5px;">CAISSE D'ENTRAIDE IPRC KARONGI</div>
              <div style="font-size: 18px; font-weight: bold; color: #000; margin-bottom: 5px;">Attendance Report</div>
              <div style="font-size: 14px; color: #000; margin-bottom: 0;">${startDate} - ${endDate}</div>
            </div>
          </div>
          <hr style="border: none; border-top: 3px solid #000; margin: 15px 0; width: 100%;">
          <div style="display: block; font-size: 12px; color: #000; margin-bottom: 15px;">
            <div style="display: block; margin-bottom: 5px;"><strong>Address:</strong> Karongi, Rwanda</div>
            <div style="display: block; margin-bottom: 5px;"><strong>Phone:</strong> 0793502827</div>
            <div style="display: block; margin-bottom: 5px;"><strong>Email:</strong> caisse.iprcwest@gmail.com</div>
          </div>
          <hr style="border: none; border-top: 1px solid #ccc; margin: 15px 0 20px 0; width: 100%;">
        </div>
        
        <div style="text-align: center; font-size: 20px; font-weight: bold; margin: 20px 0; color: #050500;">
          ATTENDANCE REPORT
        </div>
        
        <div style="margin-top: 20px; padding: 15px; background-color: #f5f5f5; border-radius: 5px; font-size: 14px;">
          <h3 style="margin-bottom: 10px;">Summary</h3>
          <p><strong>Group:</strong> ${selectedGroup?.name || 'N/A'}</p>
          <p><strong>Month:</strong> ${format(selectedMonth, 'MMMM yyyy')}</p>
          <p><strong>Total Members:</strong> ${filteredTeachers.length}</p>
          <p><strong>Scheduled Meetings:</strong> ${scheduledMeetingsCount}</p>
        </div>
        
        <table style="width: 100%; border-collapse: collapse; font-size: 10px; margin-top: 20px;">
          <thead>
            <tr>
              <th style="background-color: #4dbd07; color: white; padding: 8px; text-align: center; font-weight: 600; border: 1px solid #3d9a06;">#</th>
              <th style="background-color: #4dbd07; color: white; padding: 8px; text-align: left; font-weight: 600; border: 1px solid #3d9a06;">Member</th>
              ${dateHeaders}
            </tr>
          </thead>
          <tbody>
            ${tableRows}
          </tbody>
        </table>
        
        <div style="margin-top: 24px; padding: 15px; background-color: #f5f5f5; border-radius: 5px; font-size: 14px;">
          <strong>Total Members:</strong> ${filteredTeachers.length} | 
          <strong>Generated on:</strong> ${format(new Date(), 'MMMM d, yyyy')} at ${format(new Date(), 'h:mm a')}
        </div>
      `;
      
      document.body.appendChild(pdfDiv);
      
      // Use html2canvas to render the div
      const canvas = await html2canvas(pdfDiv, { scale: 2 });
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({ orientation: 'landscape', unit: 'px', format: [canvas.width, canvas.height] });
      pdf.addImage(imgData, 'PNG', 0, 0, canvas.width, canvas.height);
      pdf.save(`attendance_report_${format(selectedMonth, 'MMMM_yyyy')}.pdf`);
      document.body.removeChild(pdfDiv);
      
      toast.success('PDF report downloaded successfully');
    } catch (error) {
      console.error("Error generating PDF report:", error);
      toast.error("Failed to generate PDF report");
    }
  };

  const handleExportExcel = () => {
    toast.info("Excel export feature coming soon");
  };

  const handlePrint = async () => {
    try {
      const startDate = format(monthStart, 'MMMM d, yyyy');
      const endDate = format(monthEnd, 'MMMM d, yyyy');
      
      // Build table rows
      const tableRows = filteredTeachers.map((teacher: Teacher, index: number) => {
        const dateCells = monthDates.map((date) => {
          const dateKey = format(date, "yyyy-MM-dd");
          const key = `${teacher.id}-${dateKey}`;
          const isChecked = attendanceData[key] || false;
          return `<td style="border: 1px solid #ddd; padding: 6px; text-align: center;">${isChecked ? '✓' : ''}</td>`;
        }).join('');
        
        return `<tr>
          <td style="border: 1px solid #ddd; padding: 6px; text-align: center;">${index + 1}</td>
          <td style="border: 1px solid #ddd; padding: 6px; font-weight: 500;">${teacher.fullName}</td>
          ${dateCells}
        </tr>`;
      }).join('');
      
      // Build date headers
      const dateHeaders = monthDates.map((date) => {
        const dayName = format(date, "EEE");
        const dayNumber = format(date, "d");
        return `<th style="background-color: #4dbd07; color: white; padding: 8px 4px; text-align: center; font-weight: 600; border: 1px solid #3d9a06; font-size: 10px;">
          <div>${dayName.toUpperCase()}</div>
          <div style="font-weight: bold;">${dayNumber}</div>
        </th>`;
      }).join('');
      
      const htmlContent = `
        <!DOCTYPE html>
        <html>
          <head>
            <title>Attendance Report - CAISSE D'ENTRAIDE IPRC KARONGI</title>
            <style>
              * { margin: 0; padding: 0; box-sizing: border-box; }
              body { 
                font-family: Arial, sans-serif; 
                margin: 20px;
                color: #333;
              }
              .header {
                display: flex;
                align-items: center;
                margin-bottom: 15px;
              }
              .logo-container {
                margin-right: 20px;
              }
              .logo-box {
                width: 120px;
                display: flex;
                flex-direction: column;
                align-items: center;
              }
              .logo-box img {
                max-width: 120px;
                height: auto;
                object-fit: contain;
              }
              .company-info {
                flex: 1;
              }
              .company-name {
                font-size: 24px;
                font-weight: bold;
                color: #000;
                margin-bottom: 5px;
              }
              .report-title {
                font-size: 18px;
                font-weight: bold;
                color: #000;
                margin-bottom: 5px;
              }
              .date-range {
                font-size: 14px;
                color: #000;
                margin-bottom: 0;
              }
              .separator-thick {
                border: none;
                border-top: 3px solid #000;
                margin: 15px 0;
                width: 100%;
              }
              .separator-thin {
                border: none;
                border-top: 1px solid #ccc;
                margin: 15px 0 20px 0;
                width: 100%;
              }
              .contact-info {
                display: block;
                font-size: 12px;
                color: #000;
                margin-bottom: 15px;
              }
              .contact-info div {
                display: block;
                margin-bottom: 5px;
              }
              table { 
                width: 100%; 
                border-collapse: collapse; 
                margin-top: 20px;
                font-size: 10px;
              }
              th { 
                background-color: #4dbd07;
                color: white;
                padding: 8px 4px;
                text-align: center;
                font-weight: 600;
                border: 1px solid #3d9a06;
              }
              td { 
                border: 1px solid #ddd; 
                padding: 6px; 
                text-align: left;
              }
              tbody tr:nth-child(even) {
                background-color: #f9f9f9;
              }
              .summary {
                margin-top: 20px;
                padding: 15px;
                background-color: #f5f5f5;
                border-radius: 5px;
                font-size: 14px;
              }
              @media print {
                body { margin: 0; padding: 15px; }
                .summary { background: #f9f9f9 !important; }
                @page { margin: 1cm; landscape: true; }
              }
            </style>
          </head>
          <body>
            <div class="header">
              <div class="logo-container">
                <div class="logo-box">
                  <img src={getCooperativeLogoSrc()} alt="IKIMINA Logo" />
                </div>
              </div>
              <div class="company-info">
                <div class="company-name">CAISSE D'ENTRAIDE IPRC KARONGI</div>
                <div class="report-title">Attendance Report</div>
                <div class="date-range">${startDate} - ${endDate}</div>
              </div>
            </div>
            <hr class="separator-thick">
            <div class="contact-info">
              <div><strong>Address:</strong> Karongi, Rwanda</div>
              <div><strong>Phone:</strong> +250 783 610 386</div>
              <div><strong>Email:</strong> ikiminakarongicollege@gmail.com</div>
            </div>
            <hr class="separator-thin">
            
            <div style="text-align: center; font-size: 20px; font-weight: bold; margin: 20px 0; color: #050500;">
              ATTENDANCE REPORT
            </div>
            
            <div class="summary">
              <h3>Summary</h3>
              <p><strong>Group:</strong> ${selectedGroup?.name || 'N/A'}</p>
              <p><strong>Month:</strong> ${format(selectedMonth, 'MMMM yyyy')}</p>
              <p><strong>Total Members:</strong> ${filteredTeachers.length}</p>
              <p><strong>Scheduled Meetings:</strong> ${scheduledMeetingsCount}</p>
            </div>
            
            <table>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Member</th>
                  ${dateHeaders}
                </tr>
              </thead>
              <tbody>
                ${tableRows}
              </tbody>
            </table>
            
            <div class="summary" style="margin-top: 20px;">
              <strong>Total Members:</strong> ${filteredTeachers.length} | 
              <strong>Generated on:</strong> ${format(new Date(), 'MMMM d, yyyy')} at ${format(new Date(), 'h:mm a')}
            </div>
          </body>
        </html>
      `;
      
      try {
        const printWindow = window.open('', '_blank', 'width=800,height=600');
        if (!printWindow) {
          toast.error("Please allow pop-ups to print the report");
          return;
        }
        
        printWindow.document.open();
        printWindow.document.write(htmlContent);
        printWindow.document.close();
        
        // Wait for content to load, then print
        const printAfterLoad = () => {
          if (printWindow && !printWindow.closed) {
            printWindow.focus();
            printWindow.print();
          }
        };
        
        // Try multiple approaches to ensure content is loaded
        if (printWindow.document.readyState === 'complete') {
          setTimeout(printAfterLoad, 100);
        } else {
          printWindow.onload = () => setTimeout(printAfterLoad, 100);
          // Fallback timeout
          setTimeout(printAfterLoad, 500);
        }
      } catch (error) {
        console.error("Print error:", error);
        toast.error("Failed to open print window. Please check your pop-up blocker settings.");
      }
    } catch (error) {
      console.error("Error generating print report:", error);
      toast.error("Failed to generate print report");
    }
  };

  // Get meeting day name
  const meetingDayName = selectedGroup?.meetingDay 
    ? ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"][
        parseInt(selectedGroup.meetingDay) || 0
      ]
    : "Not set";

  return (
    <div className="min-h-screen w-full max-w-full overflow-x-hidden sm:px-2 px-2" style={{ backgroundColor: '#fffffb' }}>
      <div className="space-y-6 min-w-0">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <h1
            className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-bold tracking-tight"
            style={{ color: '#050500' }}
          >
            Attendance Management
          </h1>
          <p className="text-sm sm:text-base text-gray-600 mt-1">
            Track and manage member attendance for group meetings
          </p>
        </motion.div>

        {/* Select Date Range */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.1 }}
        >
          <Card>
          <CardHeader>
            <CardTitle>Select Month</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Select Month</Label>
              <div className="flex items-center gap-2 mt-2">
                <Button
                  variant="outline"
                  size="icon"
                  onClick={handlePreviousMonth}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <div className="flex-1 text-center font-medium">
                  {format(selectedMonth, "MMMM yyyy")}
                </div>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={handleNextMonth}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  onClick={handleToday}
                  className="ml-2"
                >
                  Today
                </Button>
              </div>
            </div>
            <p className="text-sm text-muted-foreground">
              Viewing: {format(selectedMonth, "MMMM yyyy")}
            </p>
          </CardContent>
        </Card>
        </motion.div>

        {/* Group Details */}
        {selectedGroup && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.2 }}
          >
            <Card>
            <CardContent className="pt-6">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Group</p>
                  <p className="font-semibold">{selectedGroup.name}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Frequency</p>
                  <p className="font-semibold">{selectedGroup.meetingFrequency || "Monthly"}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Meeting Day</p>
                  <p className="font-semibold">{meetingDayName}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Meetings</p>
                  <p className="font-semibold">{scheduledMeetingsCount} scheduled</p>
                </div>
              </div>
            </CardContent>
          </Card>
          </motion.div>
        )}

        {/* Attendance Matrix */}
        {selectedGroupId && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.3 }}
          >
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div>
                    <CardTitle>Attendance Matrix</CardTitle>
                    <CardDescription>
                      View and manage attendance for all members
                    </CardDescription>
                  </div>
                  <div className="flex items-center gap-1 sm:gap-2 flex-wrap">
                    <motion.button
                      className="h-8 sm:h-9 rounded-md px-2 sm:px-3 text-xs sm:text-sm font-semibold flex items-center justify-center cursor-pointer focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 border-2 bg-white"
                      whileHover={{ scale: 1.05, transition: { duration: 0.2 } }}
                      whileTap={{ scale: 0.95, transition: { duration: 0.1 } }}
                    >
                      {scheduledMeetingsCount} Meetings
                    </motion.button>
                    <motion.button
                      className="h-8 sm:h-9 rounded-md px-1.5 sm:px-2 md:px-3 text-[10px] sm:text-xs md:text-[13px] font-semibold flex items-center justify-center cursor-pointer focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 border-2 min-w-[60px] sm:min-w-[80px] md:min-w-[100px] flex-shrink-0 bg-white"
                      whileHover={{ scale: 1.05, transition: { duration: 0.2 } }}
                      whileTap={{ scale: 0.95, transition: { duration: 0.1 } }}
                      onClick={handleExportPDF}
                    >
                      <motion.span
                        className="flex items-center text-gray-700"
                        initial={{ x: 0 }}
                        whileHover={{ x: 2 }}
                        transition={{ duration: 0.2 }}
                      >
                        <Download className="w-3 h-3 sm:w-3.5 sm:h-3.5 md:w-4 md:h-4 mr-1 sm:mr-1.5 md:mr-2" />
                        PDF
                      </motion.span>
                    </motion.button>
                    <motion.button
                      className="h-8 sm:h-9 rounded-md px-1.5 sm:px-2 md:px-3 text-[10px] sm:text-xs md:text-[13px] font-semibold flex items-center justify-center cursor-pointer focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 border-2 min-w-[60px] sm:min-w-[80px] md:min-w-[100px] flex-shrink-0 bg-white"
                      whileHover={{ scale: 1.05, transition: { duration: 0.2 } }}
                      whileTap={{ scale: 0.95, transition: { duration: 0.1 } }}
                      onClick={handleExportExcel}
                    >
                      <motion.span
                        className="flex items-center text-gray-700"
                        initial={{ x: 0 }}
                        whileHover={{ x: 2 }}
                        transition={{ duration: 0.2 }}
                      >
                        <FileSpreadsheet className="w-3 h-3 sm:w-3.5 sm:h-3.5 md:w-4 md:h-4 mr-1 sm:mr-1.5 md:mr-2" />
                        Excel
                      </motion.span>
                    </motion.button>
                    <motion.button
                      className="h-8 sm:h-9 rounded-md px-1.5 sm:px-2 md:px-3 text-[10px] sm:text-xs md:text-[13px] font-semibold flex items-center justify-center cursor-pointer focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 border-2 min-w-[60px] sm:min-w-[80px] md:min-w-[100px] flex-shrink-0 bg-white"
                      whileHover={{ scale: 1.05, transition: { duration: 0.2 } }}
                      whileTap={{ scale: 0.95, transition: { duration: 0.1 } }}
                      onClick={handlePrint}
                    >
                      <motion.span
                        className="flex items-center text-gray-700"
                        initial={{ x: 0 }}
                        whileHover={{ x: 2 }}
                        transition={{ duration: 0.2 }}
                      >
                        <Printer className="w-3 h-3 sm:w-3.5 sm:h-3.5 md:w-4 md:h-4 mr-1 sm:mr-1.5 md:mr-2" />
                        Print
                      </motion.span>
                    </motion.button>
                  </div>
                </div>
              </CardHeader>
            <CardContent>
              {/* Search and Filter */}
              <div className="flex items-center gap-4 mb-4">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search members by name..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10"
                  />
                </div>
                <Select
                  value={selectedMemberFilter}
                  onValueChange={setSelectedMemberFilter}
                >
                  <SelectTrigger className="w-[180px]">
                    <SelectValue placeholder="All Members" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Members</SelectItem>
                    <SelectItem value="present">Present Only</SelectItem>
                    <SelectItem value="absent">Absent Only</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <p className="text-sm text-muted-foreground mb-4">
                Showing {filteredTeachers.length} of {teachers.length} members
              </p>

              {teachers.length === 0 && selectedGroupId ? (
                <div className="text-center py-8 border rounded-lg bg-yellow-50">
                  <Users className="h-12 w-12 mx-auto text-yellow-600 mb-4" />
                  <p className="text-yellow-800 font-medium">No members found for this group</p>
                  <p className="text-sm text-yellow-600 mt-2">
                    Make sure members are assigned to the selected group
                  </p>
                </div>
              ) : filteredTeachers.length === 0 && teachers.length > 0 ? (
                <div className="text-center py-8 border rounded-lg bg-gray-50">
                  <Search className="h-12 w-12 mx-auto text-gray-400 mb-4" />
                  <p className="text-gray-600 font-medium">No members match your search</p>
                  <p className="text-sm text-gray-500 mt-2">
                    Try adjusting your search or filter criteria
                  </p>
                </div>
              ) : (
                <>
              {/* Attendance Table */}
              <div className="w-full overflow-x-auto" style={{ scrollbarWidth: 'thin' }}>
                <table className="w-full border-collapse">
                  <thead>
                    <tr>
                      <th className="border p-1.5 text-left sticky left-0 w-12 font-semibold border-r-0" style={{ zIndex: 30, backgroundColor: '#f3f4f6', position: 'sticky' }}>#</th>
                      <th className="border p-1.5 text-left sticky min-w-[180px] max-w-[180px] font-semibold border-l-0" style={{ zIndex: 30, backgroundColor: '#f3f4f6', position: 'sticky', left: '48px', marginLeft: 0 }}>
                        Member
                      </th>
                      {monthDates.map((date) => {
                        const dateKey = format(date, "yyyy-MM-dd");
                        const dayName = format(date, "EEE");
                        const dayNumber = format(date, "d");
                        const isToday = format(date, "yyyy-MM-dd") === format(new Date(), "yyyy-MM-dd");
                        const isMeetingDay = selectedGroup?.meetingDay 
                          ? getDay(date) === parseInt(selectedGroup.meetingDay)
                          : false;
                        
                        return (
                          <th
                            key={dateKey}
                            className={`border p-1 text-center min-w-[60px] ${
                              isToday ? "bg-green-100" : isMeetingDay ? "bg-blue-50" : ""
                            }`}
                            style={{ zIndex: 1 }}
                          >
                            <div className="text-[10px] font-medium">
                              {dayName.toUpperCase()}
                            </div>
                            <div className="text-xs font-bold">{dayNumber}</div>
                          </th>
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredTeachers.map((teacher: Teacher, index: number) => (
                      <tr key={teacher.id}>
                        <td className="border p-1.5 sticky left-0 w-12 border-r-0" style={{ zIndex: 30, backgroundColor: '#ffffff', position: 'sticky' }}>
                          {index + 1}
                        </td>
                        <td className="border p-1.5 sticky min-w-[180px] max-w-[180px] font-medium text-sm border-l-0" style={{ zIndex: 30, backgroundColor: '#ffffff', position: 'sticky', left: '48px', marginLeft: 0 }}>
                          {teacher.fullName}
                        </td>
                        {monthDates.map((date) => {
                          const dateKey = format(date, "yyyy-MM-dd");
                          const key = `${teacher.id}-${dateKey}`;
                          const isChecked = attendanceData[key] || false;
                          const isToday = format(date, "yyyy-MM-dd") === format(new Date(), "yyyy-MM-dd");
                          const isMeetingDay = selectedGroup?.meetingDay 
                            ? getDay(date) === parseInt(selectedGroup.meetingDay)
                            : false;
                          
                          return (
                            <td
                              key={dateKey}
                              className={`border p-1 text-center ${
                                isToday ? "bg-green-100" : isMeetingDay ? "bg-blue-50" : ""
                              }`}
                              style={{ zIndex: 1 }}
                            >
                              <Checkbox
                                checked={isChecked}
                                onCheckedChange={(checked) =>
                                  handleAttendanceChange(teacher.id, date, checked as boolean)
                                }
                                className="mx-auto scale-90"
                              />
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              </>
              )}
            </CardContent>
          </Card>
          </motion.div>
        )}

        {!selectedGroupId && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.1 }}
          >
            <Card>
            <CardContent className="py-12 text-center">
              <Calendar className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">Loading group information...</p>
            </CardContent>
          </Card>
          </motion.div>
        )}
      </div>
    </div>
  );
}

